import React, { useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  X, Loader2, AlertCircle, Mail, Plus, Copy, Check, Trash2, Pencil, UserPen, ArrowRight,
  Circle, CircleDot, ListChecks, Package, MailCheck, Lock, ShieldOff, Flag,
  Briefcase, Calendar, IdCard, BadgeCheck, Info, GraduationCap, Image,
  CheckCircle2, AlertTriangle, RotateCcw, Ban,
} from 'lucide-react';
import { Button, Avatar } from '../ui-v2';
import { Modal, Badge, Input, Select } from '../ui-v2.5';
import PantallaProceso from './PantallaProceso';
import {
  BarraAcciones, FranjaFina, BandaTotal, CabeceraColumna, EstadoTarea, QuienSeEntera,
} from './piezasProceso';
import { nombreCampo } from './camposPersona';
import { RUTA_EMBUDO, rutaFicha } from './rutasProceso';
import {
  getAlta, estadoTareaAlta, crearTareaAlta, eliminarTareaAlta, estadoRecursoAlta, activosDeRecursoAlta,
  anadirRecursoAlta, quitarRecursoAlta, editarDestinatariosAlta, renotificarAlta,
  avisarDatosAlta,
  crearCredencialAlta, marcarCredencialAlta, revocarCredencialAlta, cerrarAlta, listAreas, listRecursos,
  cancelarAlta, volverAltaEnCurso,
} from './api';
import { UsuarioContext } from '../../providers/personaContext';
import DeshacerAlta from './DeshacerAlta';
import BorrarAlta from './BorrarAlta';
import { useNavigate } from 'react-router-dom';
import { IconoRecurso } from './iconosRecurso';
import { fmtFecha } from './fecha';
import EditarAltaDialog from './EditarAltaDialog';
import { avisoUnaPorPersona } from '../inventario/utils/unaPorPersona';
import SelectorResponsable from './components/SelectorResponsable';

/**
 * DetalleAlta — el seguimiento del onboarding (Fase 10 §4.4), con PANTALLA PROPIA desde el 14/10.
 *
 * Hasta entonces era `DetalleAltaDrawer`, un panel lateral encima del embudo. Ahora lo enmarca
 * `PantallaProceso` en la ruta `…/incorporaciones/altas/:id` (ver `AltaPage.jsx`), con el MISMO
 * contenido y en el mismo orden: el rediseño es otra pieza (FASE_PANTALLA_DE_ALTA_Y_BAJA.md, §6).
 *
 * Conserva la estructura que traía del drawer de solicitud de Fase 8: el `header` como nodo propio,
 * carga con bandera de cancelación y tres estados (cargando / error / datos). La cabecera es de
 * MARCA (fondo #003c66 = `v25-brand-800`, avatar, badges y barra de progreso) y aquí hay acciones.
 *
 * Las OCHO secciones del mockup, en su orden: cabecera · contexto · progreso del alta · credenciales ·
 * tareas · material · áreas avisadas · datos aportados.
 *
 * DECISIONES QUE ESTE FICHERO MATERIALIZA:
 *
 *  - **Nada de "correo pendiente".** El mockup pinta una caja ámbar diciendo que Sistemas aún no ha
 *    creado el correo. Es imposible: `mail_empresa` es obligatorio al crear el alta, así que el correo
 *    existe siempre. Lo que puede faltar es el teléfono de empresa y la extensión, y eso es lo que
 *    dice la caja.
 *  - **El timeline va compuesto en línea**, con divs y tokens. `StepIndicator` (variante `list`) es un
 *    indicador LINEAL: deriva hecho/activo/pendiente de un único puntero. Estos hitos son
 *    independientes entre sí (las credenciales pueden estar hechas con el material sin preparar), así
 *    que no cabe en ese contrato. Disposición única: si reaparece, se promueve a componente.
 *  - **El progreso viene del backend**, que lo recalcula en cada acción. No se calcula aquí en
 *    paralelo, para que no haya dos verdades.
 *  - **Datos del candidato: dos procedencias distintas.** Lo no confidencial es lo que RRHH confirmó
 *    en el wizard; lo confidencial es literal del candidato (se copió del staging sin pasar por el
 *    formulario). Se dice en la propia sección, porque quien lo lee necesita saberlo.
 *  - **Sin componentes nuevos de ui-v2.5** y sin `ToastContainer` local: `toast` va al del layout.
 *  - Solo tokens que EXISTEN en tailwind.config (`v25.ink` es 900/700/500/400/300: no hay 600 ni 800).
 */

const cuerpoError = (e) => (e && e.response && e.response.data) || {};

const TIPO_LABEL = { interno: 'Interno', colaborador: 'Colaborador' };
const ESTADO_ALTA_MAP = {
  borrador: { tone: 'info', label: 'Borrador' },
  en_curso: { tone: 'progress', label: 'En curso' },
  completada: { tone: 'active', label: 'Completada' },
  cancelandose: { tone: 'waiting', label: 'Cancelándose' },
  cancelada: { tone: 'expired', label: 'Cancelada' },
};

// Estados de seguimiento (mismo enum para tarea y material). El clic CICLA, como en el mockup.
//
// EL SPINNER SIGNIFICA UNA SOLA COSA: "hay una peticion en vuelo". Nada mas.
// `en_curso` llevaba `Loader2` + `animate-spin`, exactamente el mismo dibujo que el indicador de
// peticion en vuelo, asi que una tarea en curso YA ASENTADA se veia identica a una cargando: la
// pildora giraba para siempre, y de eso mas el porcentaje quieto se deducia que el clic no habia
// hecho nada. Si lo habia hecho: el estado quedaba escrito en BBDD. Ahora los tres estados son
// dibujos ESTATICOS y forman una progresion legible: circulo vacio -> circulo con punto -> check.
const SEG = {
  pendiente: { label: 'Pendiente', icono: Circle, clases: 'bg-v25-info-bg text-v25-info-fg border-v25-info-bd', siguiente: 'en_curso' },
  en_curso: { label: 'En curso', icono: CircleDot, clases: 'bg-v25-waiting-bg text-v25-waiting-fg border-v25-waiting-bd', siguiente: 'completada' },
  completada: { label: 'Hecho', icono: Check, clases: 'bg-v25-active-bg text-v25-active-fg border-v25-active-bd', siguiente: 'pendiente' },
};

/**
 * EL ACTIVO DE UNA LÍNEA DE MATERIAL (15/10, §17 parte 3).
 *
 * La reserva vive en el alta y la asignación en Inventario:
 *   · elegir activo con la línea pendiente o en curso lo RESERVA: es para esta persona y aún no se
 *     le ha dado. Inventario no se toca;
 *   · marcarla «Hecho» lo ASIGNA en Inventario, y desmarcarla lo DEVUELVE.
 *
 * «Reservado» y «entregado» se pintan distintos a propósito: si se leyeran igual, un reservado
 * parecería dado. Y si Inventario no lo tiene a su nombre estando entregado, se dice en ámbar.
 */
export function ActivoDelMaterial({ altaId, r, editable, ocupada, onElegir }) {
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const hecho = r.estado === 'completada';
  const a = r.activo;

  const abrir = async () => {
    setAbierto(true); setCargando(true); setError(null);
    try {
      setDatos(await activosDeRecursoAlta(altaId, r.id));
    } catch (e) {
      setError((e && e.response && e.response.data && e.response.data.message) || 'No se han podido cargar los activos.');
    } finally {
      setCargando(false);
    }
  };

  let linea = null;
  if (a && hecho && a.asignado_a_esta_persona) {
    linea = <span className="text-v25-ink-500">Entregado: <strong className="font-medium text-v25-ink-700">{a.referencia}</strong> · asignado en Inventario</span>;
  } else if (a && hecho) {
    linea = <span className="text-amber-700">Entregado: <strong className="font-medium">{a.referencia}</strong> · Inventario no lo tiene a su nombre</span>;
  } else if (a) {
    linea = <span className="text-v25-ink-500">Reservado: <strong className="font-medium text-v25-ink-700">{a.referencia}</strong> · aún sin entregar</span>;
  } else if (hecho) {
    linea = <span className="text-amber-700">Entregado sin decir cuál: Inventario no lo sabe</span>;
  }

  return (
    <div className="ml-11 mt-1 space-y-1 text-[11px]">
      <div className="flex flex-wrap items-center gap-2">
        {linea}
        {editable && !abierto && (
          <button type="button" onClick={abrir} disabled={ocupada}
            className="rounded-v25-full border border-v25-line px-2 py-0.5 font-medium text-v25-ink-600 hover:bg-v25-surface-raised disabled:opacity-50">
            {a ? 'Cambiar activo' : 'Elegir activo'}
          </button>
        )}
        {editable && a && !hecho && (
          <button type="button" onClick={() => onElegir(null)} disabled={ocupada}
            className="rounded-v25-full px-1.5 py-0.5 text-v25-ink-400 hover:text-v25-expired-fg disabled:opacity-50"
            title="Soltar la reserva: el activo queda libre para otra persona">
            Soltar
          </button>
        )}
      </div>
      {abierto && (
        <div className="flex items-center gap-2">
          {cargando ? (
            <span className="inline-flex items-center gap-1 text-v25-ink-400"><Loader2 className="h-3 w-3 animate-spin" />Buscando activos…</span>
          ) : error ? (
            <span className="text-v25-expired-fg">{error}</span>
          ) : datos && !datos.registrable ? (
            // El motivo tal cual lo da el servidor: «no se puede» sin decir por qué es una pared.
            <span className="text-amber-700">{datos.motivo || 'Este material no se puede enganchar a un activo.'}</span>
          ) : datos && datos.activos.length === 0 ? (
            <span className="text-amber-700">No hay ninguno libre en Inventario. Dalo de alta allí (sin persona) y vuelve.</span>
          ) : (
            <select defaultValue="" disabled={ocupada} aria-label={`Activo para ${r.nombre}`}
              onChange={(e) => { if (e.target.value) { onElegir(Number(e.target.value)); setAbierto(false); } }}
              className="min-w-0 flex-1 rounded-v25-md border border-v25-line px-2 py-1 text-xs">
              <option value="">{hecho ? 'Elige el que se entrega…' : 'Elige el que se le reserva…'}</option>
              {(datos ? datos.activos : []).map((x) => (
                <option key={x.id} value={x.id}>{x.referencia}{x.ya_es_suyo ? ' (ya lo tiene asignado)' : ''}</option>
              ))}
            </select>
          )}
          <button type="button" onClick={() => setAbierto(false)}
            className="shrink-0 rounded-v25-full p-0.5 text-v25-ink-400 hover:text-v25-ink-700" aria-label="Cerrar la elección de activo">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_URL;
const fotoUrl = (foto) => (foto ? `${API_URL}/api/persona/imagen/${foto}` : null);

// ───────────────────────────── piezas locales ─────────────────────────────

function Campo({ label, children }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-v25-ink-400">{label}</dt>
      <dd className="text-sm text-v25-ink-900">{children || <span className="text-v25-ink-400">—</span>}</dd>
    </div>
  );
}

/** Botón de copiar con acuse. No se reutiliza `CopiarEnlace`: ese está atado al enlace del token. */
function BotonCopiar({ texto, etiqueta }) {
  const [copiado, setCopiado] = useState(false);
  if (!texto) return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(texto);
          setCopiado(true);
          setTimeout(() => setCopiado(false), 1800);
        } catch (_) {
          toast.info('Tu navegador ha bloqueado el portapapeles; selecciona el texto a mano.');
        }
      }}
      className="shrink-0 p-1 text-v25-ink-400 hover:text-v25-brand-600"
      aria-label={`Copiar ${etiqueta}`}
    >
      {copiado ? <Check className="h-3.5 w-3.5 text-v25-ok" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

/**
 * Una credencial del alta. Tres estados y tres caminos distintos según lo que declare su recurso:
 *
 *   · `campo_persona === 'mail_empresa'` → SOLO LECTURA. Es la identidad de acceso a la intranet:
 *     la credencial pasa de prevista a creada, pero el valor lo trae la ficha de la persona y se
 *     cambia allí. Se ofrece el enlace en vez de un campo de texto.
 *   · con otro `campo_persona` → se teclea el valor, que se escribirá en esa columna.
 *   · con `campo_password` → enlace CON CANDADO a la pestaña Soporte. No se enseña nada aquí: el
 *     panel se abre con `incorporaciones.ver`, el permiso más repartido del módulo.
 */
function FilaCredencialRecurso({ cred, sinTrabajo, enVuelo, enlacePersona, enlaceSoporte, onMarcar, onDesmarcar, onRevocar }) {
  const [abierta, setAbierta] = useState(false);
  const [valor, setValor] = useState('');
  const Icono = ({ className }) => <IconoRecurso nombre={cred.icono} className={className} />;
  const esCorreo = cred.campo_persona === 'mail_empresa';
  const revocada = cred.estado === 'revocada';
  const creada = cred.estado === 'creada';

  const tono = revocada ? 'expired' : (creada ? 'active' : 'received');
  const rotuloEstado = revocada ? 'Revocada' : (creada ? 'Creada' : 'Pendiente');

  return (
    <div className="px-4 py-2.5">
      <div className="flex items-center gap-2">
        <Icono className="h-4 w-4 shrink-0 text-v25-ink-500" />
        <span className="text-sm text-v25-ink-800">{cred.nombre}</span>
        <Badge tone={tono}>{rotuloEstado}</Badge>

        {cred.campo_password && (
          <a
            href={enlaceSoporte}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-v25-ink-500 underline-offset-2 hover:text-v25-ink-800 hover:underline"
            title="La contraseña vive en la pestaña Soporte, con su guard de rol y re-autenticación"
          >
            <Lock className="h-3 w-3" />Contraseña en Soporte
          </a>
        )}

        <span className="ml-auto flex items-center gap-2">
          <span className={`text-xs ${cred.valor ? 'text-v25-ink-700' : 'text-v25-ink-400'}`}>
            {cred.valor || '—'}
          </span>
          {/* Copiar el valor: lo tenía la caja de Fase 10 y se conserva, que es de lo que más se usa. */}
          <BotonCopiar texto={cred.valor} etiqueta={cred.nombre} />
          {esCorreo && (
            <a
              href={enlacePersona}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-v25-ink-500 underline-offset-2 hover:text-v25-ink-800 hover:underline"
              title="El correo es la identidad de acceso: se cambia en la ficha de la persona"
            >
              Editar en su ficha
            </a>
          )}
        </span>
      </div>

      {/* A QUÉ CAMPO DE LA FICHA VA. Antes una nota al pie de la sección lo explicaba («marcar una
          credencial escribe su campo en la ficha»); dicho en cada fila, la nota sobra. */}
      {cred.campo_persona && (
        <div className="mt-0.5 flex items-center gap-1 pl-6 text-[11px] text-v25-brand-600">
          <ArrowRight className="h-3 w-3" />Ficha · {nombreCampo(cred.campo_persona)}
        </div>
      )}

      {!sinTrabajo && !revocada && (
        <div className="mt-1.5 flex items-center gap-2">
          {!creada && !abierta && (
            <Button size="sm" variant="outline" disabled={enVuelo} onClick={() => {
              // El correo no pide valor: se marca directamente y el backend lo toma de la columna.
              if (esCorreo || !cred.campo_persona) { onMarcar(undefined); return; }
              setValor(cred.valor || '');
              setAbierta(true);
            }}>
              {enVuelo ? <Loader2 className="animate-spin" /> : <Check />}Marcar creada
            </Button>
          )}

          {!creada && abierta && (
            <>
              <Input
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                wrapperClassName="max-w-[200px]"
                placeholder={cred.campo_persona === 'extension_telefono' ? 'Extensión' : 'Valor'}
                maxLength={cred.campo_persona === 'extension_telefono' ? 5 : 100}
              />
              <Button size="sm" disabled={enVuelo} onClick={() => { onMarcar(valor); setAbierta(false); }}>
                {enVuelo ? <Loader2 className="animate-spin" /> : <Check />}Guardar
              </Button>
              <Button size="sm" variant="ghost" disabled={enVuelo} onClick={() => setAbierta(false)}>Cancelar</Button>
            </>
          )}

          {creada && (
            <Button size="sm" variant="ghost" disabled={enVuelo} onClick={onDesmarcar}>
              {enVuelo ? <Loader2 className="animate-spin" /> : <RotateCcw />}Volver a pendiente
            </Button>
          )}

          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 hover:text-red-700" disabled={enVuelo} onClick={onRevocar}>
            <Ban />Revocar
          </Button>
        </div>
      )}

      {revocada && (
        <p className="mt-1 pl-6 text-[11px] text-v25-ink-400">
          Revocada{cred.revocada_en ? ` el ${fmtFecha(cred.revocada_en)}` : ''}.
        </p>
      )}
    </div>
  );
}

// ───────────────────────────── la pantalla ─────────────────────────────

export default function DetalleAlta({ altaId, onCambio }) {
  const [d, setD] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(false);
  // Claves de las acciones EN VUELO. Es un CONJUNTO, no un valor unico: cuando era unico,
  // `disabled={!!ocupado}` deshabilitaba TODAS las pildoras del panel mientras cualquier accion
  // estuviera en curso, y con una peticion lenta la pantalla entera parecia congelada. Ahora cada
  // fila se bloquea sola y las demas siguen pulsables.
  const [ocupado, setOcupado] = useState(() => new Set());
  const enVueloDe = (clave) => ocupado.has(clave);
  const tomar = (clave) => setOcupado((s) => new Set(s).add(clave));
  const soltar = (clave) => setOcupado((s) => { const n = new Set(s); n.delete(clave); return n; });
  const [areas, setAreas] = useState([]);

  // Alta de tarea
  const [nuevaAbierta, setNuevaAbierta] = useState(false);
  const [nuevaDesc, setNuevaDesc] = useState('');
  const [nuevaArea, setNuevaArea] = useState('');
  const [nuevaResp, setNuevaResp] = useState(null);

  // Cierre del alta (§4.5)
  const [confirmarCierre, setConfirmarCierre] = useState(false);
  // Cancelar y volver (pieza 5.2). `deshacer` lo carga la sección de deshacer y lo cuenta aquí,
  // porque «Volver a en curso» depende de que no haya nada marcado.
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [confirmarVolver, setConfirmarVolver] = useState(false);
  const [deshacer, setDeshacer] = useState(null);

  // Cancelar y volver, por permiso: los botones desaparecen, no se deshabilitan (mismo criterio
  // que el embudo, ver `Incorporaciones.jsx`). Super Administrador pasa por encima.
  const ctx = useContext(UsuarioContext) || {};
  const esSuperAdmin = (ctx.persona?.rol || '').toLowerCase() === 'super administrador';
  const puedeCancelar = esSuperAdmin
    || (!!ctx.permisosCargados && Array.isArray(ctx.permisos) && ctx.permisos.includes('incorporaciones.cancelar_alta'));
  // Borrar es más estricto que cancelar: sólo Recursos Humanos (y Super Administrador).
  const puedeBorrar = esSuperAdmin
    || (!!ctx.permisosCargados && Array.isArray(ctx.permisos) && ctx.permisos.includes('incorporaciones.borrar_alta'));
  const navigate = useNavigate();

  // Credenciales
  const [nuevaCredRecurso, setNuevaCredRecurso] = useState('');
  const [recursosCredencial, setRecursosCredencial] = useState([]);
  const [editarAbierto, setEditarAbierto] = useState(false);   // el diálogo de la fecha
  const [catalogo, setCatalogo] = useState([]);        // recursos del catálogo, para «añadir»
  const [nuevoRecurso, setNuevoRecurso] = useState('');
  // `areaAAvisar`, no `nuevaArea`: esa ya existe arriba y es el área de una NUEVA TAREA.
  // Compartirlas haría que elegir a quién avisar cambiara el formulario de tareas.
  const [areaAAvisar, setAreaAAvisar] = useState('');
  const [correoAAvisar, setCorreoAAvisar] = useState('');
  const [errorCorreo, setErrorCorreo] = useState(null);
  const [confReenvio, setConfReenvio] = useState(null);
  const [confDatos, setConfDatos] = useState(null);   // { areas:[], correo, marcadas:Set }   // { nuevos, yaAvisados, todos }

  // Derivados de las credenciales. `credenciales_lista` la sirve el detalle desde la tabla de la
  // persona; el objeto `credenciales` de Fase 10 ya no se usa aquí.
  const creds = (d && d.credenciales_lista) || [];

  // Enlaces a la ficha de la persona: el de edición para el correo, y el de la pestaña Soporte
  // (tab=3) para las credenciales que declaran dónde vive su contraseña.
  const enlacePersona = d && d.titular ? rutaFicha(d.ambito, d.titular.id) : '#';
  const enlaceSoporte = enlacePersona === '#' ? '#' : `${enlacePersona}?tab=3`;

  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) { setCargando(true); setError(false); }
    try {
      const r = await getAlta(altaId);
      setD(r);
      return r;
    } catch (e) {
      // 404 es «no existe o no está a tu alcance» (el servidor contesta 404 y no 403 a propósito,
      // para no revelar que existe). Con la URL a la vista, eso hay que decirlo distinto de «falló».
      if (!silencioso) setError(e?.response?.status === 404 ? 'no_existe' : 'fallo');
      return null;
    } finally {
      if (!silencioso) setCargando(false);
    }
  }, [altaId]);

  useEffect(() => { cargar(); }, [cargar]);



  // Catálogo de credenciales para el desplegable de "añadir". Se pide una vez al abrir el panel;
  // se filtra por tipo aquí y no en el servidor para no tocar el endpoint de recursos.
  /* UNA sola llamada alimenta los DOS desplegables: el de credenciales y el de añadir material.
   * El catálogo ya se pedía aquí para el primero; pedirlo otra vez para el segundo habría sido
   * duplicar una petición por no mirar lo que ya había. */
  useEffect(() => {
    let vivo = true;
    listRecursos()
      .then((rs) => {
        if (!vivo) return;
        const activos = (rs || []).filter((r) => r.activo);
        setRecursosCredencial(activos.filter((r) => r.tipo === 'credencial'));
        setCatalogo(activos);
      })
      .catch(() => { if (vivo) { setRecursosCredencial([]); setCatalogo([]); } });
    return () => { vivo = false; };
  }, []);

  /* Áreas activas. Se piden UNA vez al abrir el panel, no al desplegar el formulario de tarea.
   *
   * Estaban condicionadas a `nuevaAbierta` porque su único consumidor era ese formulario. Desde
   * que las áreas AVISADAS se editan, hay un segundo desplegable que se ve siempre, y con la
   * condición vieja salía con una sola opción: el placeholder. El síntoma era "no hay áreas" y la
   * causa era "no se han pedido".
   *
   * Es una lista corta y estable, igual que el catálogo de recursos que ya se pide aquí al abrir. */
  useEffect(() => {
    if (areas.length) return;
    listAreas().then((r) => setAreas((Array.isArray(r) ? r : r.areas || []).filter((a) => a.activa !== false)))
      .catch(() => setAreas([]));
  }, [nuevaAbierta, areas.length]);

  const cerrado = d && d.estado === 'completada';
  const cancelada = d && d.estado === 'cancelada';
  /* «Cancelándose» (pieza 5): el alta se está deshaciendo. No admite el trabajo normal —el
   * servidor lo rechaza desde la 5.1—, sólo sus tareas de deshacer, que van en su sección. */
  const cancelandose = d && d.estado === 'cancelandose';
  /* `sinTrabajo` gobierna el TRABAJO del alta: tareas, material y credenciales. Antes lo hacía
   * `cerrado` solo, y un alta cancelada seguía ofreciendo botones que el servidor rechazaba. */
  const sinTrabajo = cerrado || cancelada || cancelandose;
  const algoDeshecho = !!deshacer && deshacer.tareas.some((x) => x.estado !== 'pendiente');
  /* AVISAR NO SIGUE A `cerrado`, igual que en el panel de bajas y por la misma razon.
   *
   * `altasController.renotificar` y `editarAreas` rechazan solo las CANCELADAS. La pantalla
   * era mas estricta que el servidor, asi que un alta cerrada no dejaba ni reenviar ni anadir
   * un destinatario — y ese es justo el caso que motivo el endpoint: si las reglas de aviso no
   * estaban bien ese dia, el alta nacia sin areas y el aviso se perdia. Eso se descubre DESPUES
   * de cerrar, cuando alguien echa en falta el correo.
   *
   * El trabajo del alta —tareas, recursos, credenciales— SI sigue atado a `sinTrabajo`. Separar
   * «a quien se le cuenta» de «que queda por hacer» es la unica forma de abrir lo primero sin
   * abrir lo segundo.
   *
   * ── GOBIERNA DOS BOTONES, NO UNO ──────────────────────────────────────────────────────────
   * En la seccion «Areas avisadas» esta condicion abre **«Volver a avisar» Y «Avisar de los
   * datos»**. Apretarla los cierra los dos; si algun dia uno debe cerrarse al cerrar el alta, va
   * con condicion propia. El detalle, en el comentario de esa ranura. */
  /* Cancelándose tampoco avisa: el servidor lo rechaza («no se avisa de ella»), y lo que haya que
   * desmentir es una tarea de deshacer. */
  const puedeAvisar = !!d && !cancelada && !cancelandose;
  /* ── LO QUE QUEDA: tareas, material y credenciales ────────────────────────────────────────
   *
   * `d.progreso` ya viene contado con las tres cosas desde Fase C (ver `progresoAlta.js` en el
   * servidor). Aquí NO se recuenta: antes esta línea filtraba `d.tareas` a mano, y por eso
   * `todoHecho` podía ser cierto con el material sin preparar.
   *
   * El desglose es lo que permite NOMBRAR lo que falta. Con un único número no se puede decir
   * «quedan 2 recursos», y decir «quedan 2 tareas» cuando son recursos es peor que no decir nada.
   */
  const pendientesN = d ? d.progreso.pendientes : 0;
  const quedan = d && d.progreso.detalle
    ? [
      ['tarea', 'tareas', d.progreso.detalle.tareas.pendientes],
      ['recurso', 'recursos', d.progreso.detalle.recursos.pendientes],
      ['credencial', 'credenciales', d.progreso.detalle.credenciales.pendientes],
    ].filter(([, , n]) => n > 0).map(([uno, varios, n]) => `${n} ${n === 1 ? uno : varios}`)
    : [];
  const textoQuedan = quedan.length <= 1 ? quedan.join('')
    : `${quedan.slice(0, -1).join(', ')} y ${quedan[quedan.length - 1]}`;

  /* La lista con NOMBRES, para el modal de confirmación. Antes enumeraba solo las tareas, así que
   * quien cerraba un alta con material sin preparar veía una lista vacía y un «no queda nada».
   * Cada fila dice de qué tipo es: «Teléfono de empresa» sin más no distingue un recurso de una
   * tarea que se llame igual. Las credenciales revocadas NO salen: no son trabajo pendiente. */
  const pendientesLista = d ? [
    ...d.tareas.filter((t) => t.estado !== 'completada')
      .map((t) => ({ k: `t${t.id}`, tipo: 'Tarea', texto: t.descripcion, estado: t.estado })),
    ...d.recursos.filter((r) => r.estado !== 'completada')
      .map((r) => ({ k: `r${r.id}`, tipo: 'Material', texto: r.nombre, estado: r.estado })),
    ...(d.credenciales_lista || []).filter((c) => c.estado === 'pendiente')
      .map((c) => ({ k: `c${c.id}`, tipo: 'Credencial', texto: c.nombre, estado: c.estado })),
  ] : [];

  // "Todo hecho" exige que HAYA algo: con cero, "todo hecho" sería cierto por vacuidad y el botón
  // saldría destacado sin que nadie haya preparado nada. Sin nada no hay evidencia de que esté hecho.
  const todoHecho = !!d && d.progreso.total > 0 && pendientesN === 0;

  // ── acciones ──
  const avisar = (e, porDefecto) => {
    const b = cuerpoError(e);
    toast.error(b.message || porDefecto);
  };

  async function ciclarTarea(t) {
    // Solo se ignora el doble clic sobre la MISMA tarea; las otras filas quedan libres.
    const clave = `t${t.id}`;
    if (enVueloDe(clave)) return;
    tomar(clave);
    try {
      const r = await estadoTareaAlta(altaId, t.id, SEG[t.estado].siguiente);
      // Se aplica lo que DEVUELVE el backend (estado + progreso), no lo que suponíamos. Pero SOLO
      // `estado`: la acción devuelve `completada_por` como ID numérico, mientras el detalle lo
      // devuelve como objeto {id,nombre,...}. Fusionarlo entero dejaba la tarea con dos formas
      // distintas según de dónde viniera, y el subtítulo pintaba "por undefined" hasta que llegaba
      // el refresco. Los campos de auditoría los trae `cargar(true)`, ya resueltos a persona; hasta
      // entonces el subtítulo simplemente no los muestra, que es correcto y no una mentira.
      setD((prev) => ({
        ...prev,
        progreso: r.progreso,
        tareas: prev.tareas.map((x) => (x.id === t.id ? { ...x, estado: r.tarea.estado } : x)),
      }));
      await cargar(true);   // trae hitos, sellos y `completada_por` resuelto a persona
      onCambio && onCambio();
    } catch (e) {
      avisar(e, 'No se pudo cambiar el estado de la tarea.');
    } finally {
      soltar(clave);
    }
  }

  /* `destino` existe para que CONFIRMAR un «parece hecho» no sea un segundo camino: la pildora
   * cicla al siguiente estado y el boton de confirmar va directo a `completada`, pero ambos pasan
   * por aqui y por el MISMO endpoint. Un atajo propio se habria olvidado de recargar. */
  async function ciclarRecurso(r0, destino, invAssetId) {
    const clave = `r${r0.id}`;
    if (enVueloDe(clave)) return;
    tomar(clave);
    try {
      const estadoNuevo = destino || SEG[r0.estado].siguiente;
      let r;
      try {
        r = await estadoRecursoAlta(altaId, r0.id, estadoNuevo, invAssetId);
      } catch (e) {
        /* UNA POR PERSONA (01/11): si la persona ya tiene otro del tipo y el modo es «avisar», se
         * pregunta y, si se confirma, se reenvía lo mismo con la confirmación. «Bloquear» no se
         * puede confirmar: sigue al aviso de error de siempre. */
        const aviso = avisoUnaPorPersona(e);
        if (!aviso || !aviso.confirmable) throw e;
        if (!window.confirm(aviso.pregunta)) return;
        r = await estadoRecursoAlta(altaId, r0.id, estadoNuevo, invAssetId, true);
      }
      setD((prev) => ({ ...prev, recursos: prev.recursos.map((x) => (x.id === r0.id ? { ...x, ...r.recurso } : x)) }));
      /* Lo que ha pasado en INVENTARIO, dicho: marcar o desmarcar material ya no es sólo una
       * casilla, asigna y devuelve. Y las notas del servidor —«ya estaba asignado», «ya no lo
       * tenía»— explican cuando no ha pasado lo que se esperaba. */
      if ((r.eventos || []).includes('asignacion')) toast.success(`«${r0.nombre}» asignado en Inventario a esta persona.`);
      if ((r.eventos || []).includes('devolucion')) toast.info(`«${r0.nombre}» devuelto en Inventario.`);
      (r.notas || []).forEach((n) => toast.info(n));
      await cargar(true);
      onCambio && onCambio();
    } catch (e) {
      avisar(e, 'No se pudo cambiar el estado del material.');
    } finally {
      soltar(clave);
    }
  }

  /* AÑADIR MATERIAL. Hasta Fase C sólo entraba al aplicar el perfil, así que un alta creada sin
   * perfil no tenía nada que preparar y nunca podría tenerlo. El catálogo es el MISMO que ya usa
   * la pestaña de credenciales — no hacía falta endpoint nuevo para listar lo que ya se lista. */
  async function anadirMaterial() {
    const recursoId = Number(nuevoRecurso);
    if (!recursoId) { toast.error('Elige qué material quieres añadir.'); return; }
    tomar('nuevoRecurso');
    try {
      await anadirRecursoAlta(altaId, recursoId);
      setNuevoRecurso('');
      await cargar(true);
      onCambio && onCambio();
    } catch (e) {
      avisar(e, 'No se pudo añadir el material.');
    } finally {
      soltar('nuevoRecurso');
    }
  }

  /* QUITAR MATERIAL, con las dos cosas que hay que decir ANTES.
   *
   * 1. QUITAR UNO PENDIENTE SUBE EL PORCENTAJE. No porque se haya hecho nada, sino porque queda
   *    menos por hacer. El aviso lleva la cifra: de cuánto a cuánto. (Una COMPLETADA no se puede
   *    quitar — el servidor lo rechaza, porque se perdería el sello de quién y cuándo.)
   *
   * 2. QUITARLO DEL ALTA NO TOCA NADA MÁS: ni Inventario, ni las credenciales de la persona. Dice
   *    «esto no hay que prepararlo», NO «devuélvelo». Es facilísimo leerlo al revés, y quien lo
   *    lea al revés creerá que ha iniciado una devolución que nadie ha iniciado. */
  async function quitarMaterial(r) {
    const total = d.progreso.total;
    const hechas = d.progreso.hechas;
    const pctAhora = d.progreso.pct;
    const pctDespues = total > 1 ? Math.round((hechas / (total - 1)) * 100) : 0;
    const sube = pctDespues > pctAhora;
    const ok = window.confirm(
      `¿Quitar «${r.nombre}» de este alta?\n\n`
      + (sube
        ? `El progreso pasará del ${pctAhora}% al ${pctDespues}%: no porque se haya hecho nada, `
          + `sino porque queda menos por hacer.\n\n`
        : '')
      + 'Esto sólo dice que no hay que prepararlo. NO devuelve nada a Inventario ni toca las '
      + 'credenciales de la persona.',
    );
    if (!ok) return;
    tomar(`r${r.id}`);
    try {
      await quitarRecursoAlta(altaId, r.id);
      await cargar(true);
      onCambio && onCambio();
    } catch (e) {
      avisar(e, 'No se pudo quitar el material.');
    } finally {
      soltar(`r${r.id}`);
    }
  }

  /* LAS ÁREAS, EDITABLES. Se congelaban al crear y no había forma de tocarlas: si las reglas de
   * aviso no estaban bien ese día, el alta nacía sin áreas y el aviso se perdía para siempre.
   *
   * Se manda la LISTA COMPLETA, no la pieza: es un conjunto, y el servidor hace el diferencial.
   * Añadir o quitar aquí NO avisa a nadie — para eso está el botón de al lado. */
  /* La forma del correo se comprueba TAMBIEN aqui, ademas de en el servidor. No para confiar en
   * ello sino porque un correo mal escrito no da error al enviar: simplemente no llega. Avisar en
   * el momento es la unica forma de que alguien se entere. */
  /* Declarado ENCIMA de quien lo usa. Aqui la lectura ocurre dentro de un cierre que corre
   * despues, asi que no reventaba —pero la regla no puede distinguir esa clase de la que si
   * mata la pantalla, y un lint rojo que todos ignoran es peor que no tenerlo. */
  /* La lista COMPLETA, con las dos clases: `[{area_id:3}, {email:'gestoria@x.com'}]`. */
  const listaActual = () => [
    ...((d.areas || []).map((x) => ({ area_id: x.area_id }))),
    ...((d.correos || []).map((x) => ({ email: x }))),
  ];

  async function anadirCorreo() {
    const v = correoAAvisar.trim().toLowerCase();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { setErrorCorreo('No parece una direccion de correo.'); return; }
    if ((d.correos || []).some((x) => String(x).toLowerCase() === v)) { setErrorCorreo('Ese correo ya esta en la lista.'); return; }
    await cambiarAreas([...listaActual(), { email: v }]);
  }

  async function cambiarAreas(lista) {
    tomar('areas');
    try {
      await editarDestinatariosAlta(altaId, lista);
      setAreaAAvisar('');
      setCorreoAAvisar('');
      setErrorCorreo(null);
      await cargar(true);
      onCambio && onCambio();
    } catch (e) {
      avisar(e, 'No se pudieron guardar los destinatarios.');
    } finally {
      soltar('areas');
    }
  }

  /* EL REENVÍO, CON LOS NÚMEROS POR DELANTE.
   *
   * Se pregunta primero en seco —`soloNuevos` sin escribir— para poder decir a cuántos se va a
   * avisar y cuántos ya lo recibieron. Un «¿reenviar?» a secas no deja decidir: la pregunta real
   * es si el correo va a caerle otra vez a gente que ya lo tiene. */
  /* AVISAR DE LOS DATOS ASIGNADOS.
   *
   * No es un reenvio: **cada envio elige a quien va**. Las areas del alta vienen marcadas por
   * defecto —de normal son los mismos que recibieron el aviso del alta— pero se pueden desmarcar, y
   * se puede anadir un correo suelto. A veces solo hace falta una persona, o solo Marketing para
   * que hagan las tarjetas.
   *
   * Por eso no hay «solo a los nuevos» ni registro por destinatario: no hay nada que saltarse.
   */
  function prepararDatos() {
    const areas = (d.areas || []).map((a) => ({
      id: a.area_id, nombre: a.nombre, miembros: (a.miembros || []).length,
    }));
    setConfDatos({
      areas,
      // marcadas por defecto SOLO las que tienen a alguien: marcar un area vacia es marcar a nadie
      marcadas: new Set(areas.filter((a) => a.miembros > 0).map((a) => a.id)),
      correo: '',
    });
  }

  async function confirmarDatos() {
    const destinatarios = [
      ...[...confDatos.marcadas].map((id) => ({ area_id: id })),
      ...(confDatos.correo.trim() ? [{ email: confDatos.correo.trim() }] : []),
    ];
    tomar('datos');
    try {
      const r = await avisarDatosAlta(altaId, destinatarios);
      setConfDatos(null);
      /* Se dice lo que PASO, separando simulacro de bloqueo — la leccion del verde sobre un
       * bloqueo. `compuestos` y no `enviados`: en local y TEST todo es simulacro. */
      const n = (k) => Number((r && r[k]) || 0);
      if (r && r.motivo === 'areas_sin_miembros') {
        toast.warn('Las áreas elegidas no tienen miembros, así que no se ha escrito a nadie.');
      } else if (n('bloqueados') > 0 && n('enviados') === 0 && n('simulados') === 0) {
        toast.error(`No salió ningún correo: los ${n('bloqueados')} destinatario(s) quedaron bloqueados.`);
      } else if (n('enviados') > 0) {
        toast.success(`Datos comunicados a ${n('enviados')} destinatario(s).`);
      } else if (n('simulados') > 0) {
        toast.info(`Preparado para ${n('simulados')} destinatario(s). En este entorno el correo es un simulacro.`);
      } else {
        toast.warn('No se envió nada.');
      }
      if (onCambio) onCambio();
      await cargar();
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudieron comunicar los datos.');
    } finally {
      soltar('datos');
    }
  }

  function prepararReenvio() {
    /* LOS NÚMEROS SALEN DEL DETALLE, que ya trae los miembros de cada área y a quién se avisó.
     * No se pregunta al servidor para poder preguntar: eso sería una llamada para componer una
     * frase. Y son personas ÚNICAS —quien está en dos áreas recibe un correo, no dos—, así que se
     * cuentan con un Set y no sumando longitudes. */
    const miembros = new Set();
    (d.areas || []).forEach((a) => (a.miembros || []).forEach((m) => miembros.add(m.id ?? m.persona_id ?? m.email)));
    const yaAvisados = new Set((d.avisados || []).map((x) => x.persona_id ?? x));
    let nuevos = 0;
    miembros.forEach((x) => { if (!yaAvisados.has(x)) nuevos += 1; });
    setConfReenvio({ nuevos, yaAvisados: yaAvisados.size, total: miembros.size, todos: false });
  }

  async function confirmarReenvio() {
    tomar('reenvio');
    try {
      const r = await renotificarAlta(altaId, !!(confReenvio && confReenvio.todos));
      setConfReenvio(null);
      if (r.compuestos === 0) {
        toast.info(r.ya_avisados
          ? `Nadie nuevo a quien avisar: sus ${r.ya_avisados} destinatarios ya lo recibieron.`
          : 'No hay a quién avisar: las áreas no tienen miembros.');
      } else {
        toast.success(`Aviso enviado a ${r.compuestos} persona(s).`
          + (r.ya_avisados ? ` Otras ${r.ya_avisados} ya lo habían recibido y no se les ha escrito.` : ''));
      }
      await cargar(true);
    } catch (e) {
      avisar(e, 'No se pudo enviar el aviso.');
    } finally {
      soltar('reenvio');
    }
  }

  async function anadirTarea() {
    const desc = nuevaDesc.trim();
    if (!desc) { toast.error('Escribe la descripción de la tarea.'); return; }
    tomar('nueva');
    try {
      await crearTareaAlta(altaId, {
        descripcion: desc,
        area_id: nuevaArea ? Number(nuevaArea) : null,
        responsable_id: nuevaResp ? nuevaResp.id : null,
      });
      setNuevaDesc(''); setNuevaArea(''); setNuevaResp(null); setNuevaAbierta(false);
      await cargar(true);
      onCambio && onCambio();
      toast.success('Tarea añadida.');
    } catch (e) {
      avisar(e, 'No se pudo añadir la tarea.');
    } finally {
      soltar('nueva');
    }
  }

  async function quitarTarea(t) {
    // Misma clave que ciclarTarea: es la MISMA fila, así que borrar y ciclar se excluyen entre sí.
    const clave = `t${t.id}`;
    if (enVueloDe(clave)) return;
    tomar(clave);
    try {
      await eliminarTareaAlta(altaId, t.id);
      await cargar(true);
      onCambio && onCambio();
      toast.success('Tarea eliminada.');
    } catch (e) {
      avisar(e, 'No se pudo quitar la tarea.');
    } finally {
      soltar(clave);
    }
  }

  async function anadirCred() {
    const recursoId = Number(nuevaCredRecurso);
    if (!recursoId) return;
    if (enVueloDe('cred-nueva')) return;
    tomar('cred-nueva');
    try {
      await crearCredencialAlta(altaId, recursoId);
      setNuevaCredRecurso('');
      await cargar(true);
      onCambio && onCambio();
      toast.success('Credencial añadida, pendiente de crear.');
    } catch (e) {
      avisar(e, 'No se pudo añadir la credencial.');
    } finally {
      soltar('cred-nueva');
    }
  }

  // `valor` va undefined para el correo y para las credenciales sin columna de identidad: el
  // backend rechaza un valor para el correo, y sin columna no hay nada que escribir.
  async function marcarCred(c, estado, valor) {
    const clave = `cred-${c.id}`;
    if (enVueloDe(clave)) return;
    tomar(clave);
    try {
      const payload = { estado };
      if (valor !== undefined && valor !== null && valor !== '') payload.valor = valor;
      const r = await marcarCredencialAlta(altaId, c.id, payload);
      await cargar(true);
      onCambio && onCambio();
      const col = r && r.columna_escrita;
      toast.success(col
        ? `«${c.nombre}» marcada · se escribió ${col.campo} en la ficha de la persona.`
        : (estado === 'creada' ? `«${c.nombre}» marcada como creada.` : `«${c.nombre}» vuelve a pendiente.`));
    } catch (e) {
      avisar(e, 'No se pudo marcar la credencial.');
    } finally {
      soltar(clave);
    }
  }

  async function revocarCred(c) {
    const clave = `cred-${c.id}`;
    if (enVueloDe(clave)) return;
    tomar(clave);
    try {
      await revocarCredencialAlta(altaId, c.id);
      await cargar(true);
      onCambio && onCambio();
      toast.success(`«${c.nombre}» revocada. La fila se conserva.`);
    } catch (e) {
      avisar(e, 'No se pudo revocar la credencial.');
    } finally {
      soltar(clave);
    }
  }

  async function confirmarCerrar() {
    tomar('cerrar');
    try {
      const r = await cerrarAlta(altaId);
      setConfirmarCierre(false);
      await cargar(true);
      onCambio && onCambio();      // el embudo cambia de estado y el KPI de completadas del mes sube
      // `resumen_pendientes` lo calcula el servidor con la MISMA función que alimenta el modal
      // de confirmación. Antes esto decía «N tarea(s)» y callaba el material que acababa de
      // congelar: cerrar deja de admitir cambios también en los recursos.
      toast.success(r.pendientes_al_cerrar
        ? `Alta cerrada con ${r.resumen_pendientes} sin completar.`
        : 'Alta cerrada. Onboarding completo.');
    } catch (e) {
      avisar(e, 'No se pudo cerrar el alta.');
    } finally {
      soltar('cerrar');
    }
  }

  // Cancelar: pasa a «cancelándose» y el servidor genera las tareas de deshacer (pieza 5.1).
  async function confirmarCancelarAlta() {
    tomar('cancelar');
    try {
      const r = await cancelarAlta(altaId);
      setConfirmarCancelar(false);
      setDeshacer(null);
      await cargar(true);
      onCambio && onCambio();
      toast.success(r.tareas_creadas
        ? `Alta cancelándose. ${r.tareas_creadas === 1 ? 'Hay 1 cosa' : `Hay ${r.tareas_creadas} cosas`} que deshacer.`
        : 'Alta cancelándose. No se encontró nada hecho que deshacer.');
    } catch (e) {
      avisar(e, 'No se pudo cancelar el alta.');
    } finally {
      soltar('cancelar');
    }
  }

  // Volver a «en curso»: el servidor borra las tareas de deshacer y avisa si la fecha ya pasó.
  async function confirmarVolverAlta() {
    tomar('volver');
    try {
      const r = await volverAltaEnCurso(altaId);
      setConfirmarVolver(false);
      setDeshacer(null);
      await cargar(true);
      onCambio && onCambio();
      toast.success('El alta vuelve a estar en curso.');
      // El aviso de la fecha no se pierde en un toast que se va solo: se queda hasta cerrarlo.
      if (r.aviso_fecha) toast.warning(r.aviso_fecha, { autoClose: false });
    } catch (e) {
      setConfirmarVolver(false);
      avisar(e, 'No se pudo volver a «en curso».');
    } finally {
      soltar('volver');
    }
  }

  /* ── LAS TRES PARTES DEL TOTAL ───────────────────────────────────────────────────────────────
   * Del `detalle` del servidor, tal cual: credenciales, material y tareas. No se recuenta aquí — la
   * cuenta es UNA, en `progresoAlta.js`, y las revocadas ya salen de ella. Así la banda dice lo
   * mismo que el servidor por construcción. */
  const COLOR_CRED = '#0066b3';
  const COLOR_MAT = '#0ea5e9';
  const COLOR_TAR = '#7c3aed';
  const det = (d && d.progreso && d.progreso.detalle) || {};
  const parte = (k) => det[k] || { hechas: 0, total: 0 };
  const partes = [
    { clave: 'cred', nombre: 'Credenciales', ...parte('credenciales'), color: COLOR_CRED },
    { clave: 'mat', nombre: 'Material', ...parte('recursos'), color: COLOR_MAT },
    { clave: 'tar', nombre: 'Tareas', ...parte('tareas'), color: COLOR_TAR },
  ].map((p) => ({ clave: p.clave, nombre: p.nombre, hechas: p.hechas, total: p.total, color: p.color }));

  /* Quién está avisado ya. El alta trae `avisados` como `{ persona_id, email }`: con `persona_id` si
   * es una persona, y con `email` si es un correo de fuera. Los miembros traen `id`. */
  const avisadosIds = new Set(((d && d.avisados) || []).filter((x) => x.persona_id != null).map((x) => Number(x.persona_id)));
  const avisadosCorreos = new Set(((d && d.avisados) || []).filter((x) => x.email).map((x) => String(x.email).toLowerCase()));
  const estaAvisado = (m) => avisadosIds.has(Number(m.id ?? m.persona_id));
  const correoAvisado = (c) => avisadosCorreos.has(String(c).toLowerCase());
  const sinAvisar = (() => {
    if (!d) return 0;
    const personas = new Set();
    (d.areas || []).forEach((a) => (a.miembros || []).forEach((m) => { if (!estaAvisado(m)) personas.add(Number(m.id ?? m.persona_id)); }));
    return personas.size + (d.correos || []).filter((c) => !correoAvisado(c)).length;
  })();

  const estadoBadge = d ? (() => {
    const est = ESTADO_ALTA_MAP[d.estado] || { tone: 'info', label: d.estado };
    return <Badge tone={est.tone}>{est.label}</Badge>;
  })() : null;

  // ── cabecera de marca, y debajo «El proceso» ──
  /* «El proceso» (negocio, responsable, rol, origen y los hitos) va pegado a la cabecera: es lo que
   * se mira al abrir. Y NO al lado de la banda del total, que tiene que quedar encima de las tres
   * columnas que suma (lo decidió el mockup aprobado).
   *
   * El enlace «Editar» de 11 px que había junto a la fecha YA NO ESTÁ: era el fósil anotado en el §5
   * de la fase —el mismo que se quitó de la baja el 01/10 porque nadie lo encontraba—. Editar va en
   * la barra de acciones, con las demás. */
  const contexto = d ? [
    ['Negocio', d.empresa && d.empresa.nombre, d.departamento && d.departamento.nombre],
    ['Responsable', (d.responsable && d.responsable.nombre) || 'Sin asignar', null],
    ['Rol del sistema', d.rol_sistema, d.tipo_contrato],
    ['Origen', d.origen === 'solicitud' ? 'Formulario del candidato' : 'Alta manual',
      d.origen === 'solicitud' && d.solicitud ? `Enviado el ${fmtFecha(d.solicitud.enviada_en)}` : `Creada el ${fmtFecha(d.creada_en)}`],
  ] : [];

  const header = (
    <>
      <div className="bg-v25-brand-800 px-5 py-4 text-white">
        {!d ? <div className="h-16" /> : (
          <div className="flex items-start gap-3">
            <Avatar src={fotoUrl(d.titular && d.titular.foto)} name={(d.titular && d.titular.nombre) || 'Persona'}
              size="lg" className="h-14 w-14 text-sm ring-4 ring-white/15" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="truncate text-xl font-semibold">{(d.titular && d.titular.nombre) || 'Persona'}</h1>
                <Badge tone="info">{TIPO_LABEL[d.tipo_persona] || d.tipo_persona}</Badge>
                {estadoBadge}
                <span className="font-mono text-xs text-white/70">{d.numero}</span>
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-white/80">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {d.fecha_incorporacion ? `Incorpora el ${fmtFecha(d.fecha_incorporacion)}` : 'Sin fecha de incorporación'}
                </span>
                {d.puesto && <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{d.puesto.nombre}</span>}
                {d.titular && d.titular.dni && <span className="flex items-center gap-1"><IdCard className="h-3.5 w-3.5" />{d.titular.dni}</span>}
              </div>
            </div>
          </div>
        )}
      </div>
      {d && (
        <div className="grid grid-cols-2 items-start gap-x-6 gap-y-3 border-x border-b border-v25-line bg-white px-5 py-3 lg:grid-cols-4 2xl:grid-cols-[repeat(4,minmax(0,1fr))_minmax(0,2fr)]">
          {contexto.map(([rotulo, valor, nota]) => (
            <div key={rotulo} className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-v25-ink-400">{rotulo}</div>
              <div className="truncate text-sm font-medium text-v25-ink-900">{valor || '—'}</div>
              {nota && <div className="truncate text-xs text-v25-ink-500">{nota}</div>}
            </div>
          ))}
          <div className="col-span-2 min-w-0 lg:col-span-4 2xl:col-span-1">
            <div className="text-[11px] uppercase tracking-wide text-v25-ink-400">Hitos</div>
            <ol className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              {(d.hitos || []).map((h, i) => (
                <React.Fragment key={h.clave}>
                  {i > 0 && <li aria-hidden="true" className="h-px w-4 bg-v25-line-strong" />}
                  <li className="flex items-center gap-1.5 whitespace-nowrap" title={h.nota || undefined}>
                    <span className={`grid h-4 w-4 place-items-center rounded-v25-full ${h.hecho ? 'bg-v25-ok text-white' : h.final ? 'bg-v25-brand-800 text-white' : 'border-2 border-v25-line-strong bg-white'}`}>
                      {h.hecho ? <Check className="h-2.5 w-2.5" /> : h.final ? <Flag className="h-2 w-2" /> : null}
                    </span>
                    <span className={`text-xs ${h.hecho || h.final ? 'text-v25-ink-700' : 'text-v25-ink-400'}`}>{h.titulo}</span>
                    {h.fecha && <span className="text-[11px] tabular-nums text-v25-ink-400">{fmtFecha(h.fecha)}</span>}
                  </li>
                </React.Fragment>
              ))}
            </ol>
          </div>
        </div>
      )}
    </>
  );

  // ── la barra de acciones: TODAS, con forma de botón ──
  const barra = ({ aLaFicha }) => (!d ? null : (
    <BarraAcciones
      izquierda={(
        <>
          {!sinTrabajo && (
            <Button variant="outline" onClick={() => setEditarAbierto(true)}><Pencil />Editar la fecha</Button>
          )}
          {/* «Avisar de los datos» y «Volver a avisar» siguen a `puedeAvisar`, no a `cerrado`: un
              alta cerrada SÍ deja avisar (lo aceptan los endpoints). Ver el comentario de
              `puedeAvisar`. */}
          {puedeAvisar && (
            <Button variant="outline" onClick={prepararDatos} disabled={enVueloDe('datos')}
              title={(d.datos_contacto && (d.datos_contacto.correo || d.datos_contacto.telefono))
                ? 'Comunicar el correo y el teléfono asignados'
                : 'Todavía no tiene correo ni teléfono asignados'}>
              {enVueloDe('datos') ? <Loader2 className="animate-spin" /> : <IdCard />}Avisar de los datos
            </Button>
          )}
          {puedeAvisar && (
            <Button variant="outline" onClick={prepararReenvio} disabled={enVueloDe('reenvio') || !(d.areas || []).length}>
              {enVueloDe('reenvio') ? <Loader2 className="animate-spin" /> : <Mail />}Volver a avisar
              {sinAvisar > 0 && (
                <span className="ml-1 rounded-v25-full bg-amber-500 px-1.5 text-[11px] font-medium text-white">{sinAvisar} sin avisar</span>
              )}
            </Button>
          )}
          {aLaFicha && (
            <Button variant="outline" onClick={aLaFicha}><UserPen />Datos de la persona</Button>
          )}
        </>
      )}
      derecha={
        cerrado ? (
          <span className="flex items-center gap-2 text-sm text-v25-active-fg">
            <CheckCircle2 className="h-4 w-4" />Alta cerrada{d.actualizado_en ? ` el ${fmtFecha(d.actualizado_en)}` : ''}
          </span>
        ) : cancelada ? (
          <span className="flex items-center gap-2 text-sm text-v25-expired-fg"><AlertTriangle className="h-4 w-4" />Alta cancelada</span>
        ) : cancelandose ? (
          /* Volver sólo se ofrece con nada marcado: con algo hecho el servidor lo rechaza, y un
             botón que se va a rechazar hace perder el clic. Lo que la pantalla no sabe —un acceso
             revocado por otra vía— lo dice el servidor al intentarlo. */
          <span className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-2 text-sm text-v25-waiting-fg"><RotateCcw className="h-4 w-4" />Cancelándose</span>
            {puedeCancelar && deshacer && !algoDeshecho && (
              <Button variant="outline" onClick={() => setConfirmarVolver(true)} disabled={enVueloDe('volver')}>
                {enVueloDe('volver') ? <Loader2 className="animate-spin" /> : <ArrowRight />}Volver a «en curso»
              </Button>
            )}
          </span>
        ) : (
          <span className="flex flex-wrap items-center gap-2">
            {/* Cancelar va a la izquierda del cierre y en contorno rojo: no compite con «Cerrar». */}
            {/* Si la persona YA ENTRÓ —su fecha se cumplió antes de terminar el proceso—, el alta no se
                cancela (16/10): el servidor lo rechaza, y aquí se dice cuál es la salida en vez de
                ofrecer un botón que va a fallar. `titular.estado` lo trae el detalle; 2 = en proceso. */}
            {puedeCancelar && d.titular && d.titular.estado != null && Number(d.titular.estado) !== 2 && (
              <span className="max-w-xs text-xs text-v25-ink-500" title="La fecha de incorporación se cumplió antes de terminar el proceso">
                Esta persona ya entró: el alta no se cancela. Si hay que deshacerla, dale de baja.
              </span>
            )}
            {puedeCancelar && !(d.titular && d.titular.estado != null && Number(d.titular.estado) !== 2) && (
              <Button variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => setConfirmarCancelar(true)} disabled={enVueloDe('cancelar')}>
                {enVueloDe('cancelar') ? <Loader2 className="animate-spin" /> : <Ban />}Cancelar el alta
              </Button>
            )}
            {/* El cierre NO tiene vuelta: lo dice la confirmación. Destacado sólo con todo hecho; con
                pendientes es legítimo cerrar (algo puede dejar de aplicar) y la confirmación los nombra. */}
            <Button variant={todoHecho ? 'default' : 'outline'} onClick={() => setConfirmarCierre(true)} disabled={enVueloDe('cerrar')}>
              {enVueloDe('cerrar') ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}Cerrar el alta
              {pendientesN > 0 && <span className="ml-1 text-xs opacity-75">· quedan {pendientesN}</span>}
            </Button>
          </span>
        )
      }
    />
  ));

  const anadirAbajo = (contenido) => <div className="border-t border-v25-line px-4 py-2.5">{contenido}</div>;

  return (
    <>
    <PantallaProceso
      rutaListado={RUTA_EMBUDO}
      persona={d && d.titular ? { ambito: d.ambito, id: d.titular.id } : null}
      header={header}
      barra={barra}
      franjaFina={d ? <FranjaFina nombre={(d.titular && d.titular.nombre) || 'Persona'} numero={d.numero} estado={estadoBadge} /> : null}
    >
      {cargando ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-v25-ink-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Cargando…
        </div>
      ) : error === 'no_existe' ? (
        <div className="px-5 py-12 text-center text-sm text-v25-ink-700">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-v25-ink-400" />
          No existe el alta #{altaId}, o no está entre las que puedes ver.
        </div>
      ) : error || !d ? (
        <div className="px-5 py-12 text-center text-sm text-red-600">
          <AlertCircle className="mx-auto mb-2 h-6 w-6" />No se pudo cargar el detalle del alta.
          <div><Button variant="outline" className="mt-3" onClick={() => cargar()}>Reintentar</Button></div>
        </div>
      ) : (
        <>
        {/* Mientras se cancela, lo primero es lo que hay que deshacer: el resto queda debajo, quieto. */}
        {cancelandose && (
          <div className="mb-4">
            <DeshacerAlta altaId={altaId} puedeMarcar={puedeCancelar} onDatos={setDeshacer} />
            {/* Debajo, el final: qué impide borrar o, con todo listo, el borrado. `version` es lo que
                vuelve a preguntar cada vez que se marca una tarea. */}
            <div className="mt-3">
              <BorrarAlta altaId={altaId} puedeBorrar={puedeBorrar} version={deshacer}
                onBorrada={() => { onCambio && onCambio(); navigate(RUTA_EMBUDO); }} />
            </div>
          </div>
        )}
        <div className="proc-rejilla-alta">
          <div className="proc-total"><BandaTotal verbo="Preparado" partes={partes} /></div>

          {/* ── CREDENCIALES ── cada una dice a qué campo de la ficha va: sobra la nota de antes. */}
          <section className="rounded-v25-lg border border-v25-line bg-white">
            <CabeceraColumna clave="cred" color={COLOR_CRED} icono={BadgeCheck} titulo="Credenciales"
              hechas={partes[0].hechas} total={partes[0].total} />
            {creds.length === 0 ? (
              <p className="px-4 py-3 text-xs text-v25-ink-400">Sin credenciales. Se generan al aplicar el perfil, o se añaden aquí.</p>
            ) : (
              <div className="divide-y divide-v25-line">
                {creds.map((c) => (
                  <FilaCredencialRecurso
                    key={c.id}
                    cred={c}
                    sinTrabajo={sinTrabajo}
                    enVuelo={enVueloDe(`cred-${c.id}`)}
                    enlacePersona={enlacePersona}
                    enlaceSoporte={enlaceSoporte}
                    onMarcar={(valor) => marcarCred(c, 'creada', valor)}
                    onDesmarcar={() => marcarCred(c, 'pendiente')}
                    onRevocar={() => revocarCred(c)}
                  />
                ))}
              </div>
            )}
            {!sinTrabajo && recursosCredencial.length > 0 && anadirAbajo(
              <div className="flex items-center gap-2">
                <select className="min-w-0 flex-1 rounded-v25-md border border-v25-line bg-white px-2 py-1.5 text-xs"
                  value={nuevaCredRecurso} onChange={(e) => setNuevaCredRecurso(e.target.value)}
                  disabled={enVueloDe('cred-nueva')} aria-label="Credencial que se añade">
                  <option value="">Añadir credencial…</option>
                  {recursosCredencial.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                </select>
                <Button size="sm" variant="outline" disabled={!nuevaCredRecurso || enVueloDe('cred-nueva')} onClick={anadirCred}>
                  {enVueloDe('cred-nueva') ? <Loader2 className="animate-spin" /> : <Plus />}Añadir
                </Button>
              </div>,
            )}
          </section>

          {/* ── MATERIAL ── */}
          <section className="rounded-v25-lg border border-v25-line bg-white">
            <CabeceraColumna clave="mat" color={COLOR_MAT} icono={Package} titulo="Material"
              hechas={partes[1].hechas} total={partes[1].total} />
            {d.recursos.length === 0 ? (
              <p className="px-4 py-3 text-sm text-v25-ink-400">Sin material asignado.</p>
            ) : (
              <ul className="divide-y divide-v25-line">
                {d.recursos.map((r) => {
                  const enVuelo = enVueloDe(`r${r.id}`);
                  const hecho = r.estado === 'completada';
                  return (
                    <li key={r.id} className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-v25-md ${hecho ? 'bg-v25-active-bg text-v25-active-fg' : 'bg-v25-surface-raised text-v25-ink-500'}`}>
                          <IconoRecurso nombre={r.icono} className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm text-v25-ink-900">{r.nombre}</div>
                          <div className="truncate text-[11px] text-v25-ink-400">{r.area ? r.area.nombre : 'Sin área'}{r.inventariable ? ' · inventariable' : ''}</div>
                        </div>
                        <EstadoTarea estado={r.estado} disabled={sinTrabajo} ocupada={enVuelo}
                          onClick={() => ciclarRecurso(r)} etiqueta={`Cambiar el estado de: ${r.nombre}`} />
                        {/* Quitar no sale sobre lo ya hecho: el servidor lo rechaza (se perdería el sello). */}
                        {!sinTrabajo && !hecho && (
                          <button type="button" onClick={() => quitarMaterial(r)} disabled={enVuelo}
                            className="shrink-0 rounded-v25-full p-1 text-v25-ink-400 hover:bg-v25-expired-bg hover:text-v25-expired-fg disabled:opacity-50"
                            title={`Quitar «${r.nombre}» de este alta`} aria-label={`Quitar ${r.nombre}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                      {/* «Parece hecho» es una INFERENCIA; confirmarlo crea un hecho. Por eso un botón. */}
                      {r.parece_hecho === true && !hecho && (
                        <div className="ml-11 mt-1.5 flex items-center gap-2 text-[11px] text-v25-waiting-fg">
                          <span className="min-w-0 truncate" title={r.porque}>Parece hecho: {r.porque}</span>
                          <button type="button" onClick={() => ciclarRecurso(r, 'completada')} disabled={enVuelo || sinTrabajo}
                            className="shrink-0 rounded-v25-full border border-v25-waiting-bd bg-v25-waiting-bg px-2 py-0.5 font-medium disabled:opacity-50">
                            Confirmar
                          </button>
                        </div>
                      )}
                      {r.parece_hecho === null && r.porque && (
                        <div className="ml-11 mt-1 truncate text-[11px] text-v25-ink-400" title={r.porque}>No se puede comprobar: {r.porque}</div>
                      )}
                      {r.inventariable && (
                        <ActivoDelMaterial altaId={altaId} r={r} editable={!sinTrabajo} ocupada={enVuelo}
                          /* Elegir con la línea pendiente la pasa a «en curso»: reservar ES empezar a prepararlo. */
                          onElegir={(id) => ciclarRecurso(r, r.estado === 'pendiente' ? 'en_curso' : r.estado, id)} />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {!sinTrabajo && anadirAbajo(
              <div className="flex items-center gap-2">
                <select value={nuevoRecurso} onChange={(e) => setNuevoRecurso(e.target.value)}
                  className="min-w-0 flex-1 rounded-v25-md border border-v25-line px-2 py-1.5 text-xs" aria-label="Material que se añade al alta">
                  <option value="">Añadir material…</option>
                  {catalogo
                    .filter((r) => !d.recursos.some((x) => Number(x.recurso_id) === Number(r.id)))
                    .map((r) => <option key={r.id} value={r.id}>{r.nombre}{r.tipo === 'credencial' ? ' (credencial)' : ''}</option>)}
                </select>
                <Button size="sm" variant="outline" onClick={anadirMaterial} disabled={!nuevoRecurso || enVueloDe('nuevoRecurso')}>
                  {enVueloDe('nuevoRecurso') ? <Loader2 className="animate-spin" /> : <Plus />}Añadir
                </Button>
              </div>,
            )}
          </section>

          {/* ── TAREAS ── */}
          <section className="proc-tareas rounded-v25-lg border border-v25-line bg-white">
            <CabeceraColumna clave="tar" color={COLOR_TAR} icono={ListChecks} titulo="Tareas"
              hechas={partes[2].hechas} total={partes[2].total} />
            {d.tareas.length === 0 ? (
              <p className="px-4 py-3 text-sm text-v25-ink-400">Sin tareas. El perfil del puesto no traía plantilla de tareas; puedes añadirlas aquí.</p>
            ) : (
              <ul className="divide-y divide-v25-line">
                {d.tareas.map((t) => {
                  const enVuelo = enVueloDe(`t${t.id}`);
                  const hecha = t.estado === 'completada';
                  return (
                    <li key={t.id} className="flex items-center gap-3 px-4 py-2.5">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-v25-full text-[10px] font-semibold text-white"
                        style={{ background: (t.area && t.area.color) || '#64748b' }} title={t.area ? t.area.nombre : 'Responsable directo'}>
                        {t.area ? t.area.nombre.slice(0, 2).toUpperCase() : '—'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className={`truncate text-sm ${hecha ? 'text-v25-ink-400 line-through' : 'text-v25-ink-900'}`}>{t.descripcion}</div>
                        <div className="truncate text-[11px] text-v25-ink-400">
                          {t.responsable ? t.responsable.nombre : 'Sin responsable'}
                          <span className="text-v25-ink-300"> · </span>
                          {t.area ? t.area.nombre : 'Responsable directo'}
                          {t.completada_en ? ` · hecha ${fmtFecha(t.completada_en)}${t.completada_por ? ` por ${t.completada_por.nombre}` : ''}` : ''}
                        </div>
                      </div>
                      <EstadoTarea estado={t.estado} disabled={sinTrabajo} ocupada={enVuelo}
                        onClick={() => ciclarTarea(t)} etiqueta={`Cambiar el estado de: ${t.descripcion}`} />
                      {!sinTrabajo && (
                        <button type="button" onClick={() => quitarTarea(t)} disabled={enVuelo || hecha}
                          className="shrink-0 text-v25-ink-400 hover:text-red-600 disabled:opacity-30"
                          aria-label={`Quitar la tarea ${t.descripcion}`}
                          title={hecha ? 'Una tarea hecha no se puede quitar; desmárcala primero' : 'Quitar tarea'}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {!sinTrabajo && (nuevaAbierta ? (
              <div className="space-y-2 border-t border-v25-line bg-v25-surface-sunken p-3">
                <Input label="Descripción" value={nuevaDesc} maxLength={200} onChange={(e) => setNuevaDesc(e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <Select label="Área responsable" value={nuevaArea} onChange={(e) => setNuevaArea(e.target.value)}>
                    <option value="">Sin área (responsable directo)</option>
                    {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                  </Select>
                  <SelectorResponsable valor={nuevaResp} onChange={setNuevaResp} label="Responsable (opcional)" />
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={anadirTarea} disabled={enVueloDe('nueva')}>
                    {enVueloDe('nueva') ? <Loader2 className="animate-spin" /> : <Plus />}Añadir tarea
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setNuevaAbierta(false)} disabled={enVueloDe('nueva')}>Cancelar</Button>
                </div>
              </div>
            ) : anadirAbajo(
              <button type="button" onClick={() => setNuevaAbierta(true)} className="inline-flex items-center gap-1.5 text-xs font-medium text-v25-brand-600 hover:underline">
                <Plus className="h-3.5 w-3.5" />Añadir tarea
              </button>,
            ))}
          </section>

          <div className="proc-lateral">
            {/* En el alta las áreas son UNA lista guardada (`incorp_alta_areas`), todas quitables: no
                hay áreas «por lo que tienen que hacer» como en la baja. El mockup las separaba; los
                datos del alta no, y no se inventa la separación. */}
            <QuienSeEntera
              derivadas={null}
              tituloElegidas="Áreas"
              elegidas={(d.areas || []).map((a) => ({ ...a, id: a.area_id }))}
              correos={d.correos || []}
              estaAvisado={estaAvisado}
              correoAvisado={correoAvisado}
              editable={puedeAvisar}
              ocupada={enVueloDe('areas')}
              catalogo={areas}
              areaAAvisar={areaAAvisar}
              setAreaAAvisar={setAreaAAvisar}
              onAnadirArea={() => cambiarAreas([...listaActual(), { area_id: Number(areaAAvisar) }])}
              onQuitarArea={(a) => cambiarAreas(listaActual().filter((x) => x.area_id !== a.id))}
              correoAAvisar={correoAAvisar}
              setCorreoAAvisar={(v) => { setCorreoAAvisar(v); setErrorCorreo(null); }}
              errorCorreo={errorCorreo}
              onAnadirCorreo={anadirCorreo}
              onQuitarCorreo={(c) => cambiarAreas(listaActual().filter((x) => x.email !== c))}
            />
          </div>
        </div>

        <div className="mt-4">
          {d.origen === 'solicitud' && (
            <section className="rounded-v25-lg border border-v25-line bg-white">
              <CabeceraColumna icono={MailCheck} titulo="Datos aportados por el candidato"
                derecha={<span className="ml-auto"><Badge tone="received">Del formulario</Badge></span>} />
              <div className="p-4">
              {d.solicitud && d.solicitud.datos_borrados_en && (
                <div className="mb-3 flex items-start gap-2 rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3 text-xs text-v25-ink-500">
                  <ShieldOff className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    El almacén temporal de la solicitud se vació el <strong>{fmtFecha(d.solicitud.datos_borrados_en)}</strong>
                    {d.solicitud.datos_borrados_por ? <> por <strong>{d.solicitud.datos_borrados_por.nombre}</strong></> : null}
                    , al crearse el alta. Lo que se ve abajo vive ya en la ficha de la persona.
                  </span>
                </div>
              )}

              {!d.datos_candidato ? (
                <p className="text-sm text-v25-ink-400">Sin datos del candidato.</p>
              ) : (
                <>
                  <dl className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Campo label="Dirección">{d.datos_candidato.detalle.direccion}</Campo></div>
                    <Campo label="Código postal">{d.datos_candidato.detalle.codigo_postal}</Campo>
                    <Campo label="Población">{d.datos_candidato.detalle.poblacion}</Campo>
                    <Campo label="Provincia">{d.datos_candidato.detalle.provincia}</Campo>
                    <Campo label="Teletrabajo">{d.datos_candidato.detalle.direccion_teletrabajo}</Campo>
                    <Campo label="Lugar de nacimiento">{d.datos_candidato.detalle.lugar_nacimiento}</Campo>
                    <Campo label="Fecha de nacimiento">{fmtFecha(d.datos_candidato.detalle.fecha_nacimiento)}</Campo>
                    <Campo label="Teléfono personal">{d.datos_candidato.detalle.telefono_personal}</Campo>
                    <Campo label="Nº de hijos">{d.datos_candidato.detalle.hijos_count}</Campo>
                  </dl>

                  {d.datos_candidato.detalle.titulaciones.length > 0 && (
                    <div className="mt-3">
                      <dt className="mb-1 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-v25-ink-400">
                        <GraduationCap className="h-3.5 w-3.5" />Titulaciones ({d.datos_candidato.detalle.titulaciones.length})
                      </dt>
                      <ul className="divide-y divide-v25-line rounded-v25-md border border-v25-line">
                        {d.datos_candidato.detalle.titulaciones.map((t, i) => (
                          <li key={i} className="px-3 py-2 text-sm text-v25-ink-900">
                            {t.titulo || 'Titulación'}{t.tipo ? <span className="text-v25-ink-500"> · {t.tipo}</span> : null}
                            {t.centro || t.anio ? <div className="text-xs text-v25-ink-500">{[t.centro, t.anio].filter(Boolean).join(' · ')}</div> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Confidenciales: PRESENCIA, nunca el valor. estado_civil entra aquí porque vive en
                      persona_datos_rrhh, y lo que está en esa tabla exige su permiso. */}
                  <div className="mt-4 rounded-v25-md border border-v25-line bg-v25-surface-sunken p-3">
                    <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-v25-ink-700">
                      <Lock className="h-3.5 w-3.5" /> Datos confidenciales (no se muestran aquí)
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {[
                        ['IBAN', d.datos_candidato.resumen.iban],
                        ['Nº Seguridad Social', d.datos_candidato.resumen.num_seguridad_social],
                        ['Estado civil', d.datos_candidato.resumen.estado_civil],
                      ].map(([rot, hay]) => (
                        <span key={rot} className="rounded-v25-full bg-white px-2 py-0.5 text-v25-ink-700 ring-1 ring-v25-line">
                          {rot}: {hay ? 'presente' : '—'}
                        </span>
                      ))}
                      <span className="flex items-center gap-1 rounded-v25-full bg-white px-2 py-0.5 text-v25-ink-700 ring-1 ring-v25-line">
                        <Image className="h-3 w-3" />Fotos DNI: {d.datos_candidato.resumen.documentos}
                      </span>
                    </div>
                    <p className="mt-2 text-[11px] text-v25-ink-400">
                      Se consultarán en la ficha de la persona, que requiere el permiso de datos de RRHH.
                      Esa pestaña todavía no existe; esta pantalla no exige ese permiso.
                    </p>
                  </div>

                  {/* Las dos procedencias. Quien lee esto debe saber que no todo tiene el mismo origen.
                      El texto va dentro de UN span: siendo hijo directo del flex, cada <strong> se
                      convertía en un ítem más y la frase salía partida en tres columnas. */}
                  <p className="mt-3 flex items-start gap-1.5 text-[11px] text-v25-ink-400">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      Los datos de arriba son los que RRHH <strong>confirmó</strong> al crear el alta, sobre lo que
                      aportó el candidato. Los confidenciales, en cambio, se copiaron <strong>literalmente</strong> de
                      lo que él envió, sin pasar por el formulario.
                    </span>
                  </p>
                </>
              )}
              </div>
            </section>
          )}
        </div>
        </>
      )}
    </PantallaProceso>

    {/* Los diálogos van FUERA de la pantalla. Con el Drawer esto decidía quién pintaba encima (los
        dos eran `z-50` y ganaba el último en el DOM); ahora la pantalla no compite: los diálogos
        son portales `z-50` y lo único con z-index aquí es el pie fijo, `z-10`. */}
    {d && (
      <EditarAltaDialog
        open={editarAbierto}
        alta={{ id: d.id, numero: d.numero, fecha_incorporacion: d.fecha_incorporacion }}
        personaNombre={d.titular ? d.titular.nombre : null}
        estadoPersona={d.titular ? d.titular.estado : null}
        onClose={() => setEditarAbierto(false)}
        onGuardada={async () => { await cargar(true); onCambio && onCambio(); }}
      />
    )}

    {/* AVISAR DE LOS DATOS — se elige a quien va, cada vez.
        Y DICE TRES COSAS antes de pulsar, que son las que lo convierten en una decision:
          · si ya se comunico y cuando          -> para eso existe `datos_avisados_en`
          · QUE VA y QUE FALTA                  -> se manda con lo que haya, sin esperar
          · cuantas personas hay tras cada area -> marcar un area vacia es marcar a nadie */}
    {confDatos && d && (
      <Modal
        open
        onClose={() => setConfDatos(null)}
        size="md"
        title="Avisar de los datos asignados"
        subtitle={d.persona ? d.persona.nombre : undefined}
        footer={(
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfDatos(null)} disabled={enVueloDe('datos')}>Cancelar</Button>
            <Button onClick={confirmarDatos}
              disabled={enVueloDe('datos') || (confDatos.marcadas.size === 0 && !confDatos.correo.trim())}>
              {enVueloDe('datos') ? <Loader2 className="animate-spin" /> : <Mail />}Enviar
            </Button>
          </div>
        )}
      >
        <p className="text-sm text-v25-ink-700">
          {d.datos_avisados_en
            ? <><strong>Ya se comunicaron el {fmtFecha(d.datos_avisados_en)}.</strong> Puedes volver a mandarlo: no se salta a nadie.</>
            : <>Todavía <strong>no se ha comunicado</strong>.</>}
        </p>

        {/* QUE VA Y QUE FALTA. Se manda con lo que haya —avisar del correo sin esperar al teléfono
            es útil— pero se dice, para que sea una decisión y no una sorpresa. */}
        <div className="mt-3 rounded-v25-md border border-v25-line p-3 text-xs">
          <p className="mb-1 font-semibold text-v25-ink-700">Se comunicará:</p>
          <ul className="list-disc space-y-0.5 pl-4 text-v25-ink-700">
            {d.datos_contacto.correo && <li>Correo: <strong>{d.datos_contacto.correo}</strong></li>}
            {d.datos_contacto.telefono && <li>Teléfono: <strong>{d.datos_contacto.telefono}</strong></li>}
            {d.datos_contacto.extension && <li>Extensión: <strong>{d.datos_contacto.extension}</strong></li>}
          </ul>
          {(!d.datos_contacto.telefono || !d.datos_contacto.extension) && (
            <p className="mt-1.5 text-amber-800">
              Sin asignar todavía: {[!d.datos_contacto.telefono && 'teléfono', !d.datos_contacto.extension && 'extensión'].filter(Boolean).join(' y ')}.
              El correo lo dirá, y podrás volver a mandarlo cuando se asigne.
            </p>
          )}
        </div>

        <p className="mt-3 mb-1.5 text-[11px] font-medium uppercase tracking-wide text-v25-ink-400">A quién</p>
        {confDatos.areas.length === 0 ? (
          <p className="text-sm text-v25-ink-500">Este alta no tiene áreas. Escribe un correo abajo.</p>
        ) : (
          <div className="space-y-1">
            {confDatos.areas.map((a) => (
              <label key={a.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={confDatos.marcadas.has(a.id)}
                  onChange={(e) => setConfDatos((x) => {
                    const m = new Set(x.marcadas);
                    if (e.target.checked) m.add(a.id); else m.delete(a.id);
                    return { ...x, marcadas: m };
                  })}
                />
                {a.nombre}
                <span className={`text-[10px] ${a.miembros === 0 ? 'text-amber-700' : 'text-v25-ink-400'}`}>
                  · {a.miembros} pers.
                </span>
              </label>
            ))}
          </div>
        )}

        <label className="mt-3 block text-[11px] font-medium uppercase tracking-wide text-v25-ink-400">
          Y además, un correo suelto
        </label>
        <Input
          type="email"
          value={confDatos.correo}
          onChange={(e) => setConfDatos((x) => ({ ...x, correo: e.target.value }))}
          placeholder="alguien@empresa.com"
        />
        <p className="mt-2 text-[11px] text-v25-ink-400">
          Cada envío elige sus destinatarios: lo de esta vez no condiciona la siguiente.
        </p>
      </Modal>
    )}

    {/* VOLVER A AVISAR, con los números por delante.
        Un «¿reenviar?» a secas no deja decidir: la pregunta real es si el correo va a caerle otra
        vez a gente que ya lo tiene. */}
    {confReenvio && d && (
      <Modal
        open
        onClose={() => setConfReenvio(null)}
        size="sm"
        title="Volver a avisar"
        subtitle={`${d.numero}${d.titular ? ` · ${d.titular.nombre}` : ''}`}
        footer={(
          <>
            <Button variant="outline" onClick={() => setConfReenvio(null)} disabled={enVueloDe('reenvio')}>Cancelar</Button>
            <Button onClick={confirmarReenvio} disabled={enVueloDe('reenvio') || (!confReenvio.todos && confReenvio.nuevos === 0)}>
              {enVueloDe('reenvio') ? <Loader2 className="animate-spin" /> : <Mail />}Enviar
            </Button>
          </>
        )}
      >
        <div className="space-y-3 text-sm text-v25-ink-700">
          <p>
            {confReenvio.todos ? (
              <>Se escribirá a las <strong>{confReenvio.total}</strong> personas de las áreas avisadas,
                hayan recibido ya el aviso o no.</>
            ) : confReenvio.nuevos === 0 ? (
              <>No hay nadie nuevo a quien avisar: las <strong>{confReenvio.yaAvisados}</strong> personas
                de estas áreas ya lo recibieron.</>
            ) : (
              <>Se avisará a <strong>{confReenvio.nuevos}</strong>.
                {confReenvio.yaAvisados > 0 && <> Otras <strong>{confReenvio.yaAvisados}</strong> ya lo recibieron y no se les vuelve a escribir.</>}
              </>
            )}
          </p>
          {/* LA SALIDA PARA EL CASO QUE LA REGLA FINA NO CUBRE: alguien de un área ya avisada a
              quien se le ha añadido material nuevo desde entonces. Se ofrece la decisión con los
              números, en vez de inventar una regla más fina que nadie ha pedido. */}
          <label className="flex items-start gap-2 text-xs text-v25-ink-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={!!confReenvio.todos}
              onChange={(e) => setConfReenvio((x) => ({ ...x, todos: e.target.checked }))}
            />
            <span>
              Volver a avisar <strong>a todos</strong>, incluidos los que ya lo recibieron. Útil si a
              alguien se le ha añadido material o tareas desde el primer aviso.
            </span>
          </label>
          <p className="flex items-start gap-1.5 text-xs text-v25-ink-500">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            A cada persona se le escribe sólo lo suyo: el material de su área y las tareas que le tocan.
          </p>
        </div>
      </Modal>
    )}

    {confirmarCierre && d && (
      <Modal
        open
        onClose={() => setConfirmarCierre(false)}
        size="sm"
        tone={todoHecho ? 'default' : 'danger'}
        title="Cerrar el alta"
        subtitle={`${d.numero}${d.titular ? ` · ${d.titular.nombre}` : ''}`}
        footer={(
          <>
            <Button variant="outline" onClick={() => setConfirmarCierre(false)} disabled={enVueloDe('cerrar')}>Volver</Button>
            <Button
              variant={todoHecho ? 'default' : 'destructive'}
              onClick={confirmarCerrar}
              disabled={enVueloDe('cerrar')}
            >
              {enVueloDe('cerrar') ? <Loader2 className="animate-spin" /> : <CheckCircle2 />}Cerrar el alta
            </Button>
          </>
        )}
      >
        <div className="space-y-3 text-sm text-v25-ink-700">
          {/* La identidad del alta va en el CUERPO, no solo en `subtitle`: la rama `danger` de
              `Modal` (ui-v2.5) no pinta el subtítulo, así que con tareas pendientes —cuando el tono
              es danger— la confirmación no diría sobre qué alta actúa. Aquí se ve siempre, con
              cualquier tono. El defecto del componente queda reportado, sin tocarlo. */}
          <p className="rounded-v25-md border border-v25-line bg-v25-surface-sunken px-3 py-2 text-xs">
            <strong className="text-v25-ink-900">{d.numero}</strong>
            {d.titular ? <> · {d.titular.nombre}</> : null}
            {d.puesto ? <span className="text-v25-ink-500"> · {d.puesto.nombre}</span> : null}
          </p>
          {todoHecho ? (
            <p>
              Las <strong>{d.progreso.total}</strong> cosas que este alta tenía que preparar —tareas,
              material y credenciales— están hechas. El alta pasará a
              <strong> completada</strong> y dejará de admitir cambios en ninguna de las tres.
            </p>
          ) : (
            <>
              <p>
                {d.progreso.total === 0
                  ? 'Este alta no tiene nada que preparar: ni tareas, ni material, ni credenciales.'
                  : <>Quedan <strong>{textoQuedan}</strong> de <strong>{d.progreso.total}</strong> sin completar:</>}
              </p>
              {pendientesLista.length > 0 && (
                <ul className="max-h-40 space-y-1 overflow-y-auto rounded-v25-md border border-v25-line bg-v25-surface-sunken p-2">
                  {pendientesLista.map((x) => (
                    <li key={x.k} className="flex items-start gap-1.5 text-xs text-v25-ink-700">
                      <Circle className="mt-0.5 h-3 w-3 shrink-0 text-v25-ink-400" />
                      <span className="min-w-0">
                        <span className="text-v25-ink-400">{x.tipo} · </span>
                        {x.texto}
                        <span className="text-v25-ink-400"> · {SEG[x.estado] ? SEG[x.estado].label : x.estado}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              <p>Puedes cerrarlo igualmente si eso ya no aplica. Pero después no se podrá cambiar.</p>
            </>
          )}
          <p className="flex items-start gap-1.5 text-xs text-v25-ink-500">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            No hay forma de reabrir un alta cerrada.
          </p>
        </div>
      </Modal>
    )}

    {confirmarCancelar && d && (
      <Modal
        open
        onClose={() => setConfirmarCancelar(false)}
        size="sm"
        tone="danger"
        title="Cancelar el alta"
        footer={(
          <>
            <Button variant="outline" onClick={() => setConfirmarCancelar(false)} disabled={enVueloDe('cancelar')}>Volver</Button>
            <Button variant="destructive" onClick={confirmarCancelarAlta} disabled={enVueloDe('cancelar')}>
              {enVueloDe('cancelar') ? <Loader2 className="animate-spin" /> : <Ban />}Cancelar el alta
            </Button>
          </>
        )}
      >
        <div className="space-y-3 text-sm text-v25-ink-700">
          {/* La identidad en el cuerpo: la rama `danger` de `Modal` no pinta el subtítulo. */}
          <p className="rounded-v25-md border border-v25-line bg-v25-surface-sunken px-3 py-2 text-xs">
            <strong className="text-v25-ink-900">{d.numero}</strong>
            {d.titular ? <> · {d.titular.nombre}</> : null}
            {d.puesto ? <span className="text-v25-ink-500"> · {d.puesto.nombre}</span> : null}
          </p>
          <p>
            El alta pasará a <strong>cancelándose</strong>. Se crearán las tareas para deshacer lo que ya
            se hizo, si lo hay: recuperar el material entregado, revocar los accesos creados y desmentir
            los avisos enviados.
          </p>
          <p>
            Mientras se cancela, el alta no admite cambios y el proceso de activación no la dará de alta
            aunque llegue su fecha.
          </p>
          <p className="flex items-start gap-1.5 text-xs text-v25-ink-500">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Si ha sido un error, puedes volver a «en curso» mientras no se haya marcado ninguna de esas tareas.
          </p>
        </div>
      </Modal>
    )}

    {confirmarVolver && d && (() => {
      // La fecha, comparada en Madrid como la compara el servidor (`hoyMadrid`).
      const hoy = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid' }).format(new Date());
      const f = d.fecha_incorporacion ? String(d.fecha_incorporacion).slice(0, 10) : null;
      const yaLlego = !!f && f <= hoy;
      const n = deshacer ? deshacer.tareas.length : 0;
      return (
        <Modal
          open
          onClose={() => setConfirmarVolver(false)}
          size="sm"
          title="Volver a «en curso»"
          subtitle={`${d.numero}${d.titular ? ` · ${d.titular.nombre}` : ''}`}
          footer={(
            <>
              <Button variant="outline" onClick={() => setConfirmarVolver(false)} disabled={enVueloDe('volver')}>Seguir cancelando</Button>
              <Button onClick={confirmarVolverAlta} disabled={enVueloDe('volver')}>
                {enVueloDe('volver') ? <Loader2 className="animate-spin" /> : <ArrowRight />}Volver a «en curso»
              </Button>
            </>
          )}
        >
          <div className="space-y-3 text-sm text-v25-ink-700">
            <p>
              La cancelación se deshace: el alta vuelve a estar en curso, como estaba.
              {n > 0 && <> Se borran {n === 1 ? 'la tarea' : <>las <strong>{n}</strong> tareas</>} de deshacer: no se ha marcado ninguna.</>}
            </p>
            {yaLlego && (
              <p className="flex items-start gap-1.5 rounded-v25-md border border-v25-waiting-bd bg-v25-waiting-bg px-3 py-2 text-xs text-v25-waiting-fg">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Su fecha de incorporación ({fmtFecha(f)}) ya ha llegado: el proceso de activación la dará de alta esta madrugada.
              </p>
            )}
          </div>
        </Modal>
      );
    })()}
    </>
  );
}
